/* eslint-disable no-nested-ternary */
import { useMemo, useState, useEffect, useRef } from 'react'
import {
  useNavigate,
  useLocation,
  useParams,
  Outlet,
  useRouteLoaderData,
  useSearchParams,
} from 'react-router-dom'
import { styled, useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import HandshakeIcon from '@mui/icons-material/Handshake'
import SettingsIcon from '@mui/icons-material/Settings'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth'
import ListIcon from '@mui/icons-material/List'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery'
import { isBefore, isAfter } from 'date-fns'

import EventInfoBox from '../components/EventInfoBox'
import ParticipantStatusTag from '../components/ParticipantStatusTag'
import IconButtonOutlined from '../../root/components/IconButtonOutlined'
import BreadCrumbs from '../../root/components/BreadCrumbs'
import QuillEditor, { QuillEditorRef } from '../../root/components/QuillEditor'
import { HEADER_HEIGHT } from '../../root/components/Header'
import SubHeader from '../../root/components/SubHeader'
import { useCurrentUserQuery } from '../../../services/user'
import {
  useEventsTypeOptions,
  useParticipantStatusOptions,
  getOption,
  formatHumanDate,
} from '../../../utils'
import { LoaderData, EventStatus, ParticipantStatus, EventType } from '../../../types'
import type { eventLoader } from '../Events.loader'
import ListCalendar from './components/ListCalendar'
import TimeGridCalendar from './components/TimeGridCalendar'
import ResourceGridCalendar from './components/ResourceGridCalendar'
import TourCalendar from './components/TourCalendar'

const Background = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.secondaryBackground,
  height: `calc(100% - ${HEADER_HEIGHT})`,
  width: '100%',
}))

export default function ViewEvents() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('events')
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')
  const subEventId = searchParams.get('subEventId')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const quillRef = useRef<QuillEditorRef>(null)

  const [viewType, setViewType] = useState<'list' | 'calendar' | 'resource'>('list')
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (location.pathname.includes('/register') || location.pathname.includes('/sponsorship')) {
      setLoading(false)
    }
  }, [location])

  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const { data: currentUserData } = useCurrentUserQuery()
  const data = useRouteLoaderData('view-event') as LoaderData<ReturnType<typeof eventLoader>>
  const { event } = data

  const eventsTypeOptions = useEventsTypeOptions(t)
  const type = useMemo(
    () => getOption(eventsTypeOptions, undefined, event?.type),
    [eventsTypeOptions, event?.type]
  )

  const participantStatusOptions = useParticipantStatusOptions(t)
  const registrationStatus = useMemo(
    () => getOption(participantStatusOptions, undefined, event?.registration_status),
    [participantStatusOptions, event?.registration_status]
  )

  const isStaff = !!currentUserData?.is_staff

  const ResponsiveButton = isMobile ? IconButtonOutlined : Button

  return (
    <Background>
      <SubHeader>
        <BreadCrumbs
          links={[
            { name: t('Événements'), pathname: '/dashboard' },
            {
              name: t("Détail de l'événement"),
              pathname: '',
            },
          ]}
        />
        <Stack direction="row" gap="1rem">
          {isStaff ? (
            <ResponsiveButton
              startIcon={<SettingsIcon />}
              onClick={e => {
                e.preventDefault()
                navigate(`/admin/events/${eventId}/event`)
              }}
            >
              {isMobile ? <SettingsIcon /> : t('Modifier événement')}
            </ResponsiveButton>
          ) : null}

          {event?.type === EventType.Congress ? (
            <ResponsiveButton
              startIcon={<HandshakeIcon />}
              variant="contained"
              loading={loading}
              disabled={
                !event.sponsorship_request_start ||
                !event.sponsorship_request_end ||
                isBefore(new Date(), event.sponsorship_request_start) ||
                isAfter(new Date(), event.sponsorship_request_end)
              }
              onClick={e => {
                e.preventDefault()
                navigate(`/events/${eventId}/sponsorship`)
                setLoading(true)
              }}
            >
              {isMobile ? <HandshakeIcon /> : t('Prise en charge')}
            </ResponsiveButton>
          ) : null}

          {/* Register is closed no actions is possible :
          Event is not RegistrationOpen or InProgress status
          And user has not a confirmed registration status */}
          {!(
            event?.status === EventStatus.RegistrationOpen ||
            event?.status === EventStatus.InProgress
          ) && registrationStatus?.value !== ParticipantStatus.Confirmed ? (
            <Button startIcon={<InsertInvitationIcon />} disabled>
              {event?.status === EventStatus.Published
                ? t('Inscriptions à venir')
                : t('Inscription fermée')}
            </Button>
          ) : // If registration is open and user is already has a registered status we display it so it can resume registration
          // TODO avoid display registration status if canceled or payment failed ?
          registrationStatus ? (
            <LoadingButton
              startIcon={<EventAvailableIcon />}
              variant="contained"
              onClick={e => {
                e.preventDefault()
                // According to the registration status we redirect to the correct page
                if (registrationStatus.value === ParticipantStatus.Confirmed) {
                  // We display the registration status page to avoid uncessary call to register api with heavy computation
                  navigate(`/inscriptions/${eventId}`)
                } else {
                  // Registration can resume
                  navigate(`/events/${eventId}/register`)
                }
                setLoading(true)
              }}
              sx={theme => {
                const paletteColor = theme.palette.augmentColor({
                  color: {
                    500:
                      registrationStatus.value === ParticipantStatus.Cancelled
                        ? theme.palette.info.main
                        : registrationStatus.color,
                  },
                })
                return {
                  backgroundColor: paletteColor.main,
                  color: paletteColor.contrastText,
                  borderColor: paletteColor.dark,
                  '&:hover': {
                    backgroundColor: paletteColor.dark,
                  },
                }
              }}
              loading={loading}
            >
              {registrationStatus.value === ParticipantStatus.Cancelled
                ? t('Renouveler mon inscription')
                : registrationStatus.value === ParticipantStatus.Confirmed
                  ? t('Voir mon inscription')
                  : t("Reprendre l'inscription")}
            </LoadingButton>
          ) : (
            // If no registration status we display the register button
            <LoadingButton
              startIcon={<InsertInvitationIcon />}
              variant="contained"
              onClick={e => {
                e.preventDefault()
                navigate(`/events/${eventId}/register`)
                setLoading(true)
              }}
              loading={loading}
            >
              {t("S'inscrire")}
            </LoadingButton>
          )}
        </Stack>
      </SubHeader>

      <Stack
        sx={theme => ({
          background: theme.colors.whiteBackground,
          width: '100%',
          height: '100%',
          borderTop: `0.1rem solid ${theme.palette.divider}`,
          padding: '1rem',
          overflowY: 'auto',
        })}
        gap="1rem"
      >
        {event ? (
          <>
            <Stack gap="1rem" direction={{ xs: 'column', md: 'row' }} alignItems="center">
              <Stack
                sx={theme => ({
                  minWidth: { xs: '80%', sm: '60%', md: '38%', lg: '35%' },
                  minHeight: { xs: '100px', sm: '150px', md: '200px', lg: '240px' },
                  padding: {
                    xs: '45% 0px 0px',
                    sm: '30% 0px 0px',
                    md: '20% 0px 0px',
                    lg: '17% 0px 0px',
                  },
                  backgroundColor: theme.colors.greyBackground,
                  backgroundImage: event.picture ? `url(${event.picture})` : undefined,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  borderRadius: 4,
                  position: 'relative',
                })}
              >
                <ParticipantStatusTag status={registrationStatus?.value} absolute />
              </Stack>
              <Stack
                gap="1rem"
                alignItems="center"
                flex="1 0"
                height="100%"
                sx={{ width: { xs: '90%' } }}
              >
                <Stack
                  flex="1 0"
                  alignItems="center"
                  justifyContent="center"
                  width="100%"
                  sx={{ position: 'relative' }}
                >
                  {type ? (
                    <Chip
                      color="primary"
                      sx={{ backgroundColor: type.color, position: 'absolute', top: 0, right: 0 }}
                      label={type.label}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  ) : null}
                  <h3 style={{ marginTop: '2rem' }}>{event.name}</h3>
                </Stack>
                <EventInfoBox start={event.start} end={event.end} location={event.location} />
              </Stack>
            </Stack>
            {event.description && (
              <Stack
                borderRadius={2}
                padding="1rem"
                flex="1 0 auto"
                sx={theme => ({
                  backgroundColor: theme.colors.greyBackground,
                })}
              >
                <h5 style={{ marginBlock: 0 }}>{t('Informations')} :</h5>
                <QuillEditor
                  theme="bubble"
                  variant="text"
                  defaultValue={event.description}
                  ref={quillRef}
                  readOnly
                />
              </Stack>
            )}

            {event.type !== EventType.Bootcamp && (
              <Stack
                padding="1rem"
                flex="1 0 auto"
                sx={theme => ({
                  backgroundColor: theme.colors.secondaryBackground,
                })}
                borderRadius={2}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <h5>
                    {t('Programme')} : {formatHumanDate(event.start, true)} -{' '}
                    {formatHumanDate(event.end, true)}
                  </h5>
                  <Stack direction="row" gap="0.2rem">
                    <IconButtonOutlined
                      disabled={viewType === 'list'}
                      onClick={() => {
                        setViewType('list')
                      }}
                    >
                      <ListIcon />
                    </IconButtonOutlined>
                    {event.type !== EventType.Congress && (
                      <IconButtonOutlined
                        disabled={viewType === 'calendar'}
                        onClick={() => {
                          setViewType('calendar')
                        }}
                      >
                        <CalendarMonthIcon />
                      </IconButtonOutlined>
                    )}
                    {event.type === EventType.Congress && (
                      <IconButtonOutlined
                        disabled={viewType === 'resource'}
                        onClick={() => {
                          setViewType('resource')
                        }}
                      >
                        <CalendarViewMonthIcon />
                      </IconButtonOutlined>
                    )}
                  </Stack>
                </Stack>
                <Divider />

                {(function renderCalendarType() {
                  switch (viewType) {
                    case 'list':
                      return <ListCalendar data={data} />
                    case 'resource':
                      return <ResourceGridCalendar data={data} />
                    case 'calendar':
                    default:
                      if (event.type === EventType.Tour) {
                        return <TourCalendar data={data} />
                      }
                      return <TimeGridCalendar data={data} />
                  }
                })()}
              </Stack>
            )}
          </>
        ) : null}
      </Stack>

      <Outlet />
    </Background>
  )
}
